import { useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, X, Download, Camera, Share2 } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { shareOrDownloadFile } from "@/lib/share";

/**
 * Document uploader that stores an array of `{path, name, type}`.
 * Works alongside PhotoList (which is images-only) — DocList shows a filename
 * card instead of a thumbnail so PDFs, IDs, deeds, DPE reports etc. look right.
 */
export default function DocList({ items = [], onChange, testPrefix, addLabel, cameraLabel, uploading, setUploading }) {
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(items || []), { path: data.path, name: data.original_filename || file.name, type: data.content_type || file.type }]);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (path) => onChange((items || []).filter((d) => d.path !== path));

  const share = async (d) => {
    try {
      const result = await shareOrDownloadFile(d.path, d.name || d.path.split("/").pop());
      if (result === "downloaded") toast.info("File downloaded");
    } catch {
      toast.error("Share failed");
    }
  };

  const isImage = (t) => (t || "").startsWith("image/");

  return (
    <div className="space-y-2">
      {(items || []).length > 0 && (
        <ul className="space-y-1.5">
          {items.map((d, i) => (
            <li
              key={d.path}
              data-testid={`${testPrefix}-item-${i}`}
              className="flex items-center gap-3 bg-white border border-stone-200 rounded-lg px-3 py-2"
            >
              {isImage(d.type) ? (
                <img src={`${API}/files/${d.path}`} alt="" className="h-8 w-8 rounded object-cover border border-stone-200" />
              ) : (
                <FileText size={16} strokeWidth={1.5} className="text-stone-500 flex-shrink-0" />
              )}
              <span className="flex-1 min-w-0 text-sm text-stone-700 truncate">{d.name || d.path.split("/").pop()}</span>
              <button
                type="button"
                data-testid={`${testPrefix}-share-${i}`}
                onClick={() => share(d)}
                className="text-stone-400 hover:text-stone-900 transition-colors"
                title="Share"
              >
                <Share2 size={14} strokeWidth={1.5} />
              </button>
              <a
                href={`${API}/files/${d.path}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testPrefix}-open-${i}`}
                className="text-stone-400 hover:text-stone-900 transition-colors"
                title="Open"
              >
                <Download size={14} strokeWidth={1.5} />
              </a>
              <button
                type="button"
                data-testid={`${testPrefix}-remove-${i}`}
                onClick={() => remove(d.path)}
                className="text-stone-400 hover:text-red-600 transition-colors"
                title="Remove"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <label
        className={`inline-flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2 cursor-pointer hover:border-stone-400 text-stone-500 hover:text-stone-800 text-xs transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <Upload size={13} strokeWidth={1.5} />
        {uploading ? "…" : addLabel}
        <input
          ref={inputRef}
          data-testid={`${testPrefix}-upload-input`}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </label>
      {/* Direct-to-camera on iPhone/iPad (capture is ignored on desktop) */}
      <label
        className={`inline-flex items-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2 cursor-pointer hover:border-stone-400 text-stone-500 hover:text-stone-800 text-xs transition-colors ml-2 ${uploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <Camera size={13} strokeWidth={1.5} />
        {uploading ? "…" : cameraLabel}
        <input
          data-testid={`${testPrefix}-camera-input`}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </label>
    </div>
  );
}
